import { useState, useEffect } from 'react';
import { useLocation } from 'wouter';
import { useAuth } from '@/hooks/use-auth';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { playGameSound, preloadGameSounds } from '@/utils/sound-utils';
import { SoundCategory } from '@/services/mobile-audio';
import { useGameNotifications } from '@/hooks/use-game-notifications';
import { UserDeck } from '@/types/deck';
import { deckStorageService } from '@/services/deck-storage-service';


import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";

import { DeckFilters } from './deck-filters';
import { DeckStats } from './deck-stats';
import { DeckEmptyState } from './deck-empty-state';
import { DeckGrid } from './deck-grid';

// Helper for filtering decks
const getTabMatchFunction = (tab: string) => (userDeck: UserDeck): boolean => {
  switch (tab) {
    case "default":
      return userDeck.isDefault;
    case "free":
      return userDeck.deck.membership === "free";
    case "all":
    default:
      return true;
  }
};

export function GameSettings() {
  const [_, setLocation] = useLocation();
  const { token } = useAuth();
  const { showNotification } = useGameNotifications();
  const queryClient = useQueryClient();
  
  // UI State
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [selectedType, setSelectedType] = useState<string>("all");
  const [activeTab, setActiveTab] = useState<string>("all");

  // Preload sound effects
  useEffect(() => {
    preloadGameSounds([SoundCategory.UI, SoundCategory.Gameplay])
      .then(() => console.log('Game settings sounds preloaded'))
      .catch(err => console.error('Failed to preload sounds:', err));
  }, []);
  
  // Fetch user's card decks
  const { data: userDecks, isLoading: decksLoading } = useQuery({
    queryKey: ["/api/deck/my-decks"],
    queryFn: async () => {
      playGameSound('ui.notification');
      let apiUrl = "/api/deck/my-decks";
      if (Capacitor.isNativePlatform() && import.meta.env.VITE_SERVER_URL) {
        apiUrl = `${import.meta.env.VITE_SERVER_URL}${apiUrl}`;
      }
      const res = await fetch(apiUrl, {
        headers: { Authorization: `Bearer ${token}` },
        cache: "no-store",
      });
      if (!res.ok) throw new Error("Failed to fetch decks");
      const data = await res.json();
      return data.map((deck: any) => ({
        ...deck,
        isDefault: deck.default === true || deck.isDefault === true
      }));
    },
  });
  
  // Set default deck mutation
  const setDefaultDeckMutation = useMutation({
    mutationFn: async (deckId: number) => {
      let apiUrl = `/api/deck/set-default/${deckId}`;
      if (Capacitor.isNativePlatform() && import.meta.env.VITE_SERVER_URL) {
        apiUrl = `${import.meta.env.VITE_SERVER_URL}${apiUrl}`;
      }
      const res = await fetch(apiUrl, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message ?? "Failed to set default deck");
      }
      return res.json();
    },
    onSuccess: async (_, deckId) => {
      playGameSound('ui.success');
      showNotification(
        "Default Deck Updated",
        "Your default card deck has been updated",
        "info",
        3000
      );
      queryClient.invalidateQueries({ queryKey: ["/api/deck/my-decks"] });
      
      // Download the deck when it's set as default
      if (Capacitor.isNativePlatform()) {
        try {
          
          // Start downloading the deck
          deckStorageService.queueDeckDownload(deckId, (progress) => {
            if (progress === 100) {
              showNotification(
                "Deck Downloaded",
                "Default deck is ready for offline use!",
                "success",
                3000
              );
            }
          });
        } catch (error) {
          // Properly log the actual error
          console.error('Failed to download default deck:', error instanceof Error ? error.message : error);
        }
      }
    },
    onError: (error: any) => {
      playGameSound('ui.error');
      showNotification(
        "Error",
        error.message ?? "Failed to update default deck",
        "error",
        5000
      );
    },
  });

  // Event Handlers
  const handleViewDeck = (deckId: number) => {
    playGameSound('ui.button');
    setLocation(`/deck/${deckId}?returnTo=settings`);
  };

  const handleSetAsDefault = (deckId: number) => {
    playGameSound('ui.toggle');
    setDefaultDeckMutation.mutate(deckId);
  };

  const handleReset = () => {
    setSearchQuery("");
    setSelectedType("all");
  };

  // Get deck types for filtering
  const deckTypes: string[] = userDecks
    ? Array.from(new Set(userDecks.map((deck: UserDeck) => deck.deckType ?? "standard")))
    : [];

  // Filter decks based on criteria
  const filteredDecks = userDecks ? userDecks.filter((userDeck: UserDeck) => {
    const matchesSearch = searchQuery 
      ? userDeck.name.toLowerCase().includes(searchQuery.toLowerCase())
      : true;
    const matchesType = selectedType === "all" 
      ? true 
      : (userDeck.deckType ?? "standard").toLowerCase() === selectedType;
    const matchesTab = getTabMatchFunction(activeTab)(userDeck);
    return matchesSearch && matchesType && matchesTab;
  }) : [];
  
  // Loading state
  if (decksLoading) {
    return (
      <div className="min-h-[300px] flex justify-center items-center p-8">
        <div className="flex flex-col items-center">
          <RefreshCw className="h-12 w-12 animate-spin text-primary mb-4" />
          <p className="text-muted-foreground">Loading card decks...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div>
      <div className="px-4 pt-2">
        <div className="flex flex-col items-center justify-between mb-6">
          <Tabs defaultValue="all" className="w-full mb-6" onValueChange={setActiveTab}>
            <TabsList className="w-full bg-black/40 border border-white/10 rounded-lg p-1">
              <TabsTrigger value="all" className="flex-1 data-[state=active]:bg-primary data-[state=active]:text-white">
                All Decks
              </TabsTrigger>
              <TabsTrigger value="default" className="flex-1 data-[state=active]:bg-amber-600 data-[state=active]:text-white">
                Default
              </TabsTrigger>
              
            </TabsList>

            <DeckFilters
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              viewMode={viewMode}
              setViewMode={setViewMode}
              selectedType={selectedType}
              setSelectedType={setSelectedType}
              deckTypes={deckTypes}
            />

            <DeckStats
              filteredDecks={filteredDecks}
              selectedType={selectedType}
              searchQuery={searchQuery}
              userDecks={userDecks}
            />

            {['all', 'default', 'free'].map((tabValue) => (
              <TabsContent key={tabValue} value={tabValue}>
                {filteredDecks.length === 0 ? (
                  <DeckEmptyState 
                    type={tabValue as 'all' | 'default' | 'free'}
                    searchQuery={searchQuery}
                    onReset={handleReset}
                    onAction={() => setLocation('/deck-marketplace')}
                  />
                ) : (
                  <DeckGrid
                    decks={filteredDecks}
                    viewMode={viewMode}
                    onView={handleViewDeck}
                    onSetDefault={handleSetAsDefault}
                  />
                )}
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </div>
    </div>
  );
}